import React, { useState, useEffect, useCallback } from "react";
import { ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Field, SelectShell, inputStyles, selectStyles } from "../../components/crm/formControls";
import { getStates, getDistricts, advancedSearch } from "../../services/ejagritiApi";

import SelectMenu from "../../components/crm/SelectMenu";
const TABS = [
  { id: "status", label: "Case Status" },
  { id: "history", label: "Case History" },
  { id: "advanced", label: "Advanced Search" },
];

const COMMISSION_TYPES = [
  { value: "NCDRC", label: "NCDRC" },
  { value: "SCDRC", label: "SCDRC" },
  { value: "DCDRC", label: "DCDRC" },
];

// Mirrors the e-Jagriti "Search By" dropdown, mapped to backend search modes.
// INDUSTRY TYPE / JUDGE are routed through free-text (best effort; backend has
// no dedicated mode for them yet).
const SEARCH_BY = [
  { value: "CASE_NUMBER", label: "CASE NUMBER", mode: "CASE_NUMBER", field: "caseNumber" },
  { value: "COMPLAINANT", label: "COMPLAINANT / APPELLANT / PETITIONER", mode: "PARTY_NAME", field: "complainantName" },
  { value: "RESPONDENT", label: "RESPONDENT / OPPOSITE PARTY", mode: "PARTY_NAME", field: "respondentName" },
  { value: "COMPLAINANT_ADV", label: "COMPLAINANT / APPELLANT / PETITIONER ADVOCATE", mode: "ADVOCATE_NAME", field: "advocateName" },
  { value: "RESPONDENT_ADV", label: "RESPONDENT / OPPOSITE PARTY ADVOCATE", mode: "ADVOCATE_NAME", field: "advocateName" },
  { value: "INDUSTRY", label: "INDUSTRY TYPE", mode: "FREE_TEXT", field: "freeText" },
  { value: "JUDGE", label: "JUDGE", mode: "FREE_TEXT", field: "freeText" },
];

const SEARCH_TIPS = [
  "Case Status & History need a full case number",
  "Advanced Search filters by commission and date range",
  "Results are fetched live from e-Jagriti",
];

const isoDate = (d) => d.toISOString().slice(0, 10);
const defaultDates = () => {
  const today = new Date();
  const from = new Date(today);
  from.setFullYear(from.getFullYear() - 1);
  return { fromDate: isoDate(from), toDate: isoDate(today) };
};

const Radio = ({ name, checked, onChange, label }) => (
  <label className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
    <input type="radio" name={name} checked={checked} onChange={onChange} className="accent-yellow-500" />
    {label}
  </label>
);

export default function EJagritiSearch() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("status");

  // Case Status / History
  const [caseNumber, setCaseNumber] = useState("");

  // Advanced — commission
  const [commissionType, setCommissionType] = useState("NCDRC");
  const [stateCommissionId, setStateCommissionId] = useState("");
  const [districtCommissionId, setDistrictCommissionId] = useState("");
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  // Advanced — search config
  const [scope, setScope] = useState("FREE_TEXT"); // FREE_TEXT | ADVANCE
  const [caseDateType, setCaseDateType] = useState("FILING"); // FILING | DISPOSAL
  const [searchBy, setSearchBy] = useState("CASE_NUMBER");
  const [orderType, setOrderType] = useState("DAILY_ORDER"); // DAILY_ORDER | JUDGEMENT
  const [viewAs, setViewAs] = useState("TABLE"); // TABLE | PDF
  const [freeText, setFreeText] = useState("");
  const [advValue, setAdvValue] = useState("");
  const [dates, setDates] = useState(() => defaultDates());
  const [loading, setLoading] = useState(false);

  const needsState = commissionType === "SCDRC" || commissionType === "DCDRC";
  const needsDistrict = commissionType === "DCDRC";
  const selectedSearchBy = SEARCH_BY.find((o) => o.value === searchBy) || SEARCH_BY[0];

  useEffect(() => {
    if (needsState && !states.length) getStates().then(setStates).catch(() => toast.error("Failed to load states"));
  }, [needsState, states.length]);

  useEffect(() => {
    if (needsDistrict && stateCommissionId) {
      setDistricts([]);
      setDistrictCommissionId("");
      getDistricts(stateCommissionId).then(setDistricts).catch(() => toast.error("Failed to load districts"));
    }
  }, [needsDistrict, stateCommissionId]);

  const goToCase = (e) => {
    e.preventDefault();
    if (!caseNumber.trim()) return toast.error("Please enter a case number");
    navigate("/portal/ejagriti/case", { state: { caseNumber: caseNumber.trim(), initialTab: activeTab } });
  };

  const handleAdvanced = useCallback(
    async (e) => {
      e.preventDefault();
      if (needsState && !stateCommissionId) return toast.error("Please select a state commission");
      if (needsDistrict && !districtCommissionId) return toast.error("Please select a district commission");

      const payload = {
        commissionType,
        viewMode: viewAs,
        fromDate: dates.fromDate,
        toDate: dates.toDate,
        dateRequestType: caseDateType === "DISPOSAL" ? 2 : 1,
        page: 1,
        size: 30,
      };
      if (needsState) payload.stateCommissionId = Number(stateCommissionId);
      if (needsDistrict) payload.districtCommissionId = Number(districtCommissionId);

      let queryLabel = "";
      if (scope === "FREE_TEXT") {
        if (!freeText.trim()) return toast.error("Enter free text to search");
        payload.searchType = "FREE_TEXT";
        payload.freeText = freeText.trim();
        queryLabel = freeText.trim();
      } else {
        if (!advValue.trim()) return toast.error(`Enter ${selectedSearchBy.label.toLowerCase()}`);
        payload.searchMode = selectedSearchBy.mode;
        payload[selectedSearchBy.field] = advValue.trim();
        if (selectedSearchBy.mode !== "FREE_TEXT") payload.orderType = orderType === "JUDGEMENT" ? 2 : 1;
        queryLabel = advValue.trim();
      }

      setLoading(true);
      try {
        const data = await advancedSearch(payload);
        if (viewAs === "PDF" && data.pdfExport?.downloadUrl) {
          window.open(data.pdfExport.downloadUrl, "_blank", "noopener,noreferrer");
        }
        const stateName = states.find((s) => String(s.commissionId) === String(stateCommissionId))?.commissionNameEn;
        navigate("/portal/ejagriti/results", {
          state: { data, summary: { commissionType, stateName, searchBy: scope === "ADVANCE" ? selectedSearchBy.label : "Free Text", query: queryLabel } },
        });
      } catch (err) {
        toast.error(err.message || "Search failed");
      } finally {
        setLoading(false);
      }
    },
    [commissionType, scope, caseDateType, searchBy, orderType, viewAs, stateCommissionId, districtCommissionId, freeText, advValue, dates, needsState, needsDistrict, states, selectedSearchBy, navigate]
  );

  const activeTabLabel = TABS.find((t) => t.id === activeTab)?.label || "";
  const onSubmit = activeTab === "advanced" ? handleAdvanced : goToCase;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Consumer Court (e-Jagriti)</span>
        </div>

        {/* Stepper */}
        <div className="mt-4"><FindCaseStepper current={2} /></div>

        {/* Form Card — full width */}
        <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <form onSubmit={onSubmit}>
            <div className="p-5 sm:p-6">
              {/* Search By pills */}
              <p className="mb-2 text-center text-xs font-medium text-slate-600">Search By</p>
              <div className="flex flex-wrap justify-center gap-2">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                      activeTab === t.id
                        ? "bg-yellow-500 text-slate-900 shadow-sm"
                        : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <h2 className="mt-6 text-sm font-semibold text-slate-800">
                eJagriti : Search by {activeTabLabel}
              </h2>

              {/* Fields */}
              <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* Case Status / History */}
                {(activeTab === "status" || activeTab === "history") && (
                  <Field label="Case Number" className="sm:col-span-2">
                    <input
                      type="text"
                      value={caseNumber}
                      onChange={(e) => setCaseNumber(e.target.value)}
                      placeholder="e.g. SC/28/A/113/2024"
                      className={inputStyles}
                      autoFocus
                    />
                    <p className="mt-1.5 text-xs text-slate-500">
                      {activeTab === "status" ? "View current status, hearings and orders." : "View the full case timeline."}
                    </p>
                  </Field>
                )}

                {/* Advanced Search — mirrors e-Jagriti advance-case-search */}
                {activeTab === "advanced" && (
                  <>
                    <Field label="Commission" required={false}>
                      <div className="flex h-10 flex-wrap items-center gap-4">
                        {COMMISSION_TYPES.map((c) => (
                          <Radio
                            key={c.value}
                            name="commission"
                            checked={commissionType === c.value}
                            onChange={() => { setCommissionType(c.value); setStateCommissionId(""); setDistrictCommissionId(""); }}
                            label={c.label}
                          />
                        ))}
                      </div>
                    </Field>

                    {needsState && (
                      <Field label="State Commission">
                        <SelectShell>
                          <SelectMenu chevron={false} className={selectStyles} value={stateCommissionId} onChange={(e) => setStateCommissionId(e.target.value)}>
                            <option value="">Select state…</option>
                            {states.map((s) => <option key={s.commissionId} value={s.commissionId}>{s.commissionNameEn}</option>)}
                          </SelectMenu>
                        </SelectShell>
                      </Field>
                    )}

                    {needsDistrict && (
                      <Field label="District Commission">
                        <SelectShell>
                          <SelectMenu chevron={false} className={selectStyles} value={districtCommissionId} onChange={(e) => setDistrictCommissionId(e.target.value)} disabled={!stateCommissionId}>
                            <option value="">Select district…</option>
                            {districts.map((d) => <option key={d.commissionId} value={d.commissionId}>{d.commissionNameEn}</option>)}
                          </SelectMenu>
                        </SelectShell>
                      </Field>
                    )}

                    <Field label="From Date">
                      <input type="date" className={inputStyles} value={dates.fromDate} onChange={(e) => setDates({ ...dates, fromDate: e.target.value })} />
                    </Field>
                    <Field label="To Date">
                      <input type="date" className={inputStyles} value={dates.toDate} onChange={(e) => setDates({ ...dates, toDate: e.target.value })} />
                    </Field>

                    <Field label="Search Mode" required={false}>
                      <div className="flex h-10 flex-wrap items-center gap-4">
                        <Radio name="scope" checked={scope === "FREE_TEXT"} onChange={() => setScope("FREE_TEXT")} label="Free Text" />
                        <Radio name="scope" checked={scope === "ADVANCE"} onChange={() => setScope("ADVANCE")} label="More Advance" />
                      </div>
                    </Field>

                    {scope === "FREE_TEXT" && (
                      <Field label="Free Text Search" className="sm:col-span-2">
                        <input className={inputStyles} value={freeText} onChange={(e) => setFreeText(e.target.value)} placeholder="Free Text Search By Complainant / Respondent / Industry Name" />
                      </Field>
                    )}

                    {scope === "ADVANCE" && (
                      <>
                        <Field label="Case Filing/Disposed">
                          <SelectShell>
                            <SelectMenu chevron={false} className={selectStyles} value={caseDateType} onChange={(e) => setCaseDateType(e.target.value)}>
                              <option value="FILING">CASE FILING DATE</option>
                              <option value="DISPOSAL">CASE DISPOSAL DATE</option>
                            </SelectMenu>
                          </SelectShell>
                        </Field>
                        <Field label="Search By">
                          <SelectShell>
                            <SelectMenu chevron={false} className={selectStyles} value={searchBy} onChange={(e) => { setSearchBy(e.target.value); setAdvValue(""); }}>
                              {SEARCH_BY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </SelectMenu>
                          </SelectShell>
                        </Field>
                        <Field label="Order Type">
                          <SelectShell>
                            <SelectMenu chevron={false} className={selectStyles} value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                              <option value="DAILY_ORDER">DAILY ORDER</option>
                              <option value="JUDGEMENT">JUDGEMENT</option>
                            </SelectMenu>
                          </SelectShell>
                        </Field>
                        <Field label={selectedSearchBy.label} className="sm:col-span-2">
                          <input className={inputStyles} value={advValue} onChange={(e) => setAdvValue(e.target.value)} placeholder={selectedSearchBy.label} />
                          {(searchBy === "INDUSTRY" || searchBy === "JUDGE") && (
                            <p className="mt-1 text-[11px] text-slate-400">Searched via free text (dedicated backend support pending).</p>
                          )}
                        </Field>
                      </>
                    )}

                    <Field label="View As" required={false}>
                      <div className="flex h-10 flex-wrap items-center gap-4">
                        <Radio name="viewAs" checked={viewAs === "TABLE"} onChange={() => setViewAs("TABLE")} label="Table" />
                        <Radio name="viewAs" checked={viewAs === "PDF"} onChange={() => setViewAs("PDF")} label="PDF" />
                      </div>
                    </Field>
                  </>
                )}
              </div>
            </div>

            {/* Footer — tips + action */}
            <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <ul className="flex flex-wrap items-center gap-x-5 gap-y-1">
                {SEARCH_TIPS.map((tip) => (
                  <li key={tip} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-400" />
                    {tip}
                  </li>
                ))}
              </ul>
              <div className="flex shrink-0 gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="h-10 rounded-lg bg-yellow-500 px-8 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
